import React, { createContext, useCallback, useContext, useState, ReactNode } from 'react';

interface TermsModalContextType {
  showTermsModal: boolean;
  termsVersions: { termsVersion: string; signupTermsVersion: string } | null;
  isTermsUpdate: boolean;
  pendingRequest: (() => Promise<any>) | null;
  onCancel: (() => void) | null;
  openTermsModal: (
    termsVersion: string | null,
    signupTermsVersion: string | null,
    isUpdate: boolean,
    retryRequest: () => Promise<any>,
    onCancel?: () => void
  ) => void;
  closeTermsModal: () => void;
  acceptTerms: () => Promise<void>;
  setTermsVersions: (versions: { termsVersion: string; signupTermsVersion: string } | null) => void;
}

const TermsModalContext = createContext<TermsModalContextType | undefined>(undefined);

export const TermsModalProvider: React.FC<{ children: ReactNode }> = ({ children }) => {
  const [showTermsModal, setShowTermsModal] = useState(false);
  const [termsVersions, setTermsVersions] = useState<{ termsVersion: string; signupTermsVersion: string } | null>(null);
  const [isTermsUpdate, setIsTermsUpdate] = useState(false);
  const [pendingRequest, setPendingRequest] = useState<(() => Promise<any>) | null>(null);
  const [onCancel, setOnCancel] = useState<(() => void) | null>(null);

  const openTermsModal = useCallback((
    termsVersion: string | null,
    signupTermsVersion: string | null,
    isUpdate: boolean,
    retryRequest: () => Promise<any>,
    onCancelCallback?: () => void
  ) => {
    if (termsVersion && signupTermsVersion) {
      setTermsVersions({ termsVersion, signupTermsVersion });
    } else {
      setTermsVersions(null);
    }
    setIsTermsUpdate(isUpdate);
    setPendingRequest(() => retryRequest);
    setOnCancel(onCancelCallback || null);
    setShowTermsModal(true);
  }, []);

  const closeTermsModal = () => {
    setShowTermsModal(false);
    setTermsVersions(null);
    setPendingRequest(null);
    setOnCancel(null);
  };

  const acceptTerms = async () => {
    // This will be called from the modal to trigger acceptance
    // The actual API call will be handled by the modal component
  };

  return (
    <TermsModalContext.Provider
      value={{
        showTermsModal,
        termsVersions,
        isTermsUpdate,
        pendingRequest,
        onCancel,
        openTermsModal,
        closeTermsModal,
        acceptTerms,
        setTermsVersions,
      }}
    >
      {children}
    </TermsModalContext.Provider>
  );
};

export const useTermsModal = () => {
  const context = useContext(TermsModalContext);
  if (!context) {
    throw new Error('useTermsModal must be used within TermsModalProvider');
  }
  return context;
};
